import { TooltipProvider } from "@radix-ui/react-tooltip";
import copy from "copy-to-clipboard";
import { ComponentPropsWithoutRef, forwardRef, useCallback, useState } from "react";

import { cn } from "../utils";
import { Icon } from "./icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const INITIAL_CONTENT = "Copy";
const ACTIVE_CONTENT = "Copied!";

export const useCopy = (label = INITIAL_CONTENT) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<string>(label);

  const onCopy = useCallback((value: string | number) => {
    copy(`${value}`);
    setOpen(true);
    setContent(ACTIVE_CONTENT);
    setTimeout(() => {
      setOpen(false);
    }, 2000);
    setTimeout(() => {
      setContent(INITIAL_CONTENT);
    }, 2500);
  }, []);

  return {
    onCopy,
    copyLabel: content,
    tooltip: {
      open,
      onOpenChange: setOpen,
    },
    copied: content === ACTIVE_CONTENT,
  };
};

export const Copy = forwardRef<
  HTMLSpanElement,
  ComponentPropsWithoutRef<"span"> & { value: string | number | null | undefined }
>(({ value, ...props }, ref) => {
  const { onCopy, copyLabel, copied, tooltip } = useCopy();
  const handleClick = useCallback(() => {
    if (typeof value === "string") onCopy(value);
  }, [onCopy, value]);

  if (value === null || value === undefined) return null;

  return (
    <TooltipProvider>
      <Tooltip {...tooltip}>
        <span
          ref={ref}
          {...props}
          className={cn(
            "hover:bg-accent group inline-flex cursor-pointer items-center rounded-md p-1 transition-transform active:scale-95",
            props.className,
          )}
          onClick={handleClick}
        >
          <span data-component="value">{value}</span>
          <TooltipTrigger>
            <Icon name="copy" className="h-3 opacity-0 group-hover:opacity-100" />
          </TooltipTrigger>
        </span>
        <TooltipContent variant={copied ? "success" : "default"}>{copyLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
